import { TableCell, TableRow, Dialog, DialogTitle, DialogContent, IconButton, Select, MenuItem, Chip, TextField, CircularProgress, Button } from '@mui/material';
import { Close, Image as ImageIcon, CheckCircle, EditRounded, RemoveRedEye, HourglassEmpty, Edit, Check, Cancel, Visibility, BugReport, VideoCall, Link as LinkIcon, VolumeUp, Email, Download } from '@mui/icons-material';
import { EyeOutlined } from '@ant-design/icons';
import React, { memo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { handleUpdateFeedback, handleCreateJiraTicket } from 'store/feedback/thunks';
import { handleGetGlobalUserDetail } from 'store/organizations/thunks';
import GlobalUserDetails from 'containers/admin/OwePages/GlobalUserDetails';
import ImageModal from 'components/Modals/imageModal';

const NotesTextField = styled(TextField)`
  width: 100%;
  
  input {
    font-size: 14px;
    color: #000;
    
    &::placeholder {
      color: rgb(0, 0, 0);
    }
  }
`;
const FeedbackTableRow = ({ row, sequenceNo, onUpdateItem }) => {
  const showAlert = useAlert();
  const [openDialog, setOpenDialog] = useState(false);
  const [openScreenshotDialog, setOpenScreenshotDialog] = useState(false);
  const [notes, setNotes] = useState(row?.notes || '');
  const [status, setStatus] = useState(row?.status || 'Not started');
  const [meetingUrl, setMeetingUrl] = useState(row?.meetingUrl || '');
  const [jiraUrl, setJiraUrl] = useState(row?.jiraUrl || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingMeetingUrl, setEditingMeetingUrl] = useState(false);
  const [editingJiraUrl, setEditingJiraUrl] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [creatingJiraTicket, setCreatingJiraTicket] = useState(false);

  useEffect(() => {
    setNotes(row?.notes || '');
    setStatus(row?.status || 'Not started');
    setMeetingUrl(row?.meetingUrl || '');
    setJiraUrl(row?.jiraUrl || '');
  }, [row]);

  const formatDate = (dateValue) => {
    if (!dateValue && dateValue !== 0) return '-';
    
    let date;
    try {
      if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else if (typeof dateValue === 'string') {
        const numValue = parseInt(dateValue, 10);
        if (!isNaN(numValue)) {
          date = new Date(numValue);
        } else {
          date = new Date(dateValue);
        }
      } else {
        return '-';
      }
      
      if (isNaN(date.getTime()) || !date.getTime()) {
        return '-';
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (e) {
      console.error('Error formatting date:', e, dateValue);
      return '-';
    }
  };

  const getSystemInfo = () => {
    if (!row.systemInfo) return null;
    try {
      return JSON.parse(row.systemInfo);
    } catch (e) {
      return null;
    }
  };

  const getScreenshotUrls = () => {
    if (!row.screenshotUrl) return [];
    try {
      const parsed = typeof row.screenshotUrl === 'string' ? JSON.parse(row.screenshotUrl) : row.screenshotUrl;
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      return Array.isArray(row.screenshotUrl) ? row.screenshotUrl : [row.screenshotUrl];
    }
  };

  const systemInfo = getSystemInfo();
  const contentPreview = row.content ? (row.content.length > 50 ? row.content.substring(0, 50) + '...' : row.content) : '-';
  const screenshotUrls = getScreenshotUrls();

  const handleStatusChange = async (newStatus) => {
    const oldStatus = status;
    setStatus(newStatus);
    setIsUpdating(true);
    
    try {
      const updateData = {
        status: newStatus,
        notes: notes,
        meetingUrl: meetingUrl,
        jiraUrl: jiraUrl
      };
      
      const result = await handleUpdateFeedback(row.id, updateData);
      
      if (result && result.data) {
        showAlert('Status updated successfully', 'success');
        if (onUpdateItem) {
          onUpdateItem(row.id, { status: newStatus });
        }
      } else {
        showAlert('Failed to update status', 'error');
        setStatus(oldStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert('Error updating status', 'error');
      setStatus(oldStatus);
    } finally {
      setIsUpdating(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    if (!userId) {
      showAlert('User ID not available', 'error');
      return;
    }
    setLoadingUserDetails(true);
    try {
      const fetchUserDetailsResult = await handleGetGlobalUserDetail(userId);
      setUserDetails(fetchUserDetailsResult);
      setOpenUserDetailsModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      showAlert('Failed to fetch user details', 'error');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleNotesBlur = async () => {
    const currentNotes = (notes || '').trim();
    const originalNotes = (row?.notes || '').trim();
    
    if (currentNotes === originalNotes) return;
    
    setIsUpdating(true);
    const oldNotes = row?.notes || '';
    
    try {
      const updateData = {
        status: status,
        notes: currentNotes,
        meetingUrl: meetingUrl,
        jiraUrl: jiraUrl
      };
      
      const result = await handleUpdateFeedback(row.id, updateData);
      
      if (result && result.data) {
        showAlert('Notes updated successfully', 'success');
        if (onUpdateItem) {
          onUpdateItem(row.id, { notes: currentNotes });
        }
      } else {
        showAlert('Failed to update notes', 'error');
        setNotes(oldNotes);
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      showAlert('Error updating notes', 'error');
      setNotes(oldNotes);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusIcon = (statusText) => {
    switch (statusText) {
      case 'Completed':
        return <CheckCircle style={{ fontSize: 16 }} />;
      case 'In progress':
        return <EditRounded style={{ fontSize: 16 }} />;
      case 'Under review':
        return <RemoveRedEye style={{ fontSize: 16 }} />;
      case 'Not started':
        return <HourglassEmpty style={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (statusText) => {
    switch (statusText) {
      case 'Completed':
        return { bg: '#22c55e', color: 'white' };
      case 'In progress':
        return { bg: '#fbbf24', color: 'black' };
      case 'Under review':
        return { bg: '#60a5fa', color: 'white' };
      case 'Not started':
        return { bg: '#9ca3af', color: 'white' };
      default:
        return { bg: '#e5e7eb', color: 'black' };
    }
  };

  return (
    <>
    <TableRow
      key={row.id || `row-${sequenceNo}`}
      style={{
        '&:lastChild td, &:lastChild th': { border: 0 },
      }}
    >
        <StyledTableCell>{sequenceNo}.</StyledTableCell>
        <StyledTableCell>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EyeOutlined 
            style={{ fontSize: 18, color: '#17a2b8', cursor: 'pointer' }} 
              onClick={() => setOpenDialog(true)}
            />
          <span>{row.name || '-'}</span>
        </div>
        </StyledTableCell>
        <StyledTableCell>{row.username || '-'}</StyledTableCell>
        <StyledTableCell>{row.userRole || '-'}</StyledTableCell>
      <StyledTableCell>{formatDate(row.createdAt)}</StyledTableCell>
        <StyledTableCell>
          <ContentPreview>{contentPreview}</ContentPreview>
        </StyledTableCell>
        <StyledTableCell>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {screenshotUrls.length > 0 ? (
              <ScreenshotButton onClick={() => setOpenScreenshotDialog(true)}>
                <ImageIcon style={{ fontSize: 20, color: '#1976d2', cursor: 'pointer' }} />
                {screenshotUrls.length > 1 && <span style={{ marginLeft: 5, fontSize: 12 }}>({screenshotUrls.length})</span>}
              </ScreenshotButton>
            ) : (
              '-'
            )}
            {row.audioUrl && (
              <ScreenshotButton 
                title="Audio Recording"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(row.audioUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                <VolumeUp style={{ fontSize: 20, color: '#9b4dca', cursor: 'pointer' }} />
              </ScreenshotButton>
            )}
            {row.email && (
              <ScreenshotButton 
                title={`Email: ${row.email}`}
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `mailto:${row.email}`;
                }}
              >
                <Email style={{ fontSize: 20, color: '#4caf50', cursor: 'pointer' }} />
              </ScreenshotButton>
            )}
            {row.systemDataUrl && (
              <ScreenshotButton 
                title="Download System Data JSON"
                onClick={(e) => {
                  e.stopPropagation();
                  const link = document.createElement('a');
                  link.href = row.systemDataUrl;
                  link.download = `system-data-${row.id}.json`;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download style={{ fontSize: 20, color: '#ff9800', cursor: 'pointer' }} />
              </ScreenshotButton>
            )}
            {jiraUrl && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(jiraUrl, '_blank', 'noopener,noreferrer');
                }}
                style={{ 
                  padding: '4px',
                  color: '#0052CC'
                }}
                title="Jira Ticket Created"
              >
                <BugReport style={{ fontSize: 18 }} />
              </IconButton>
            )}
          {meetingUrl && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(meetingUrl, '_blank', 'noopener,noreferrer');
              }}
              style={{ 
                padding: '4px',
                color: '#1976d2'
              }}
              title="Meeting URL Added"
            >
              <VideoCall style={{ fontSize: 18 }} />
            </IconButton>
          )}
          </div>
        </StyledTableCell>
      <StyledTableCell>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-start' }}>
          <Select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 170,
              height: 32,
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',

              '& .MuiSelect-select': {
                padding: 0,
                lineHeight: '32px',
                fontSize: '12px',
                textAlign: 'center',
              },

              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            renderValue={(selected) => (
              <Chip
                icon={getStatusIcon(selected)}
                label={selected}
                style={{
                  backgroundColor: getStatusColor(selected).bg,
                  color: getStatusColor(selected).color,
                  height: 28,
                }}
              />
            )}
          >
            <MenuItem value="Not started">
              <Chip
                icon={<HourglassEmpty style={{ fontSize: 16 }} />}
                label="Not started"
                style={{ backgroundColor: '#9e9e9e', color: 'white' }}
              />
            </MenuItem>
            <MenuItem value="In progress">
              <Chip
                icon={<EditRounded style={{ fontSize: 16 }} />}
                label="In progress"
                style={{ backgroundColor: '#ffc107', color: 'black' }}
              />
            </MenuItem>
            <MenuItem value="Under review">
              <Chip
                icon={<RemoveRedEye style={{ fontSize: 16 }} />}
                label="Under review"
                style={{ backgroundColor: '#2196f3', color: 'white' }}
              />
            </MenuItem>
            <MenuItem value="Completed">
              <Chip
                icon={<CheckCircle style={{ fontSize: 16 }} />}
                label="Completed"
                style={{ backgroundColor: '#4caf50', color: 'white' }}
              />
            </MenuItem>
          </Select>
        </div>
      </StyledTableCell>
      <StyledTableCell>
      <NotesTextField
          placeholder="Add here"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          variant="standard"
          size="small"
          disabled={isUpdating}
          InputProps={{
            disableUnderline: true,
          }}
        />    
      </StyledTableCell>
    </TableRow>

      <Dialog 
        open={openDialog} 
        onClose={(event, reason) => {
          // Prevent closing during updates
          if (isUpdating) {
            return;
          }
          // Close any open input boxes first
          if (editingNotes) {
            setNotes(row?.notes || '');
            setEditingNotes(false);
            return; // Don't close modal, just exit edit mode
          }
          if (editingMeetingUrl) {
            setMeetingUrl(row?.meetingUrl || '');
            setEditingMeetingUrl(false);
            return; // Don't close modal, just exit edit mode
          }
          if (editingJiraUrl) {
            setJiraUrl(row?.jiraUrl || '');
            setEditingJiraUrl(false);
            return; // Don't close modal, just exit edit mode
          }
          setOpenDialog(false);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            maxWidth: '1200px',
            width: '90vw'
          }
        }}
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '20px', fontWeight: 600 }}>Feedback Report Details</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {row?.userId && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={loadingUserDetails ? <CircularProgress size={16} /> : <Visibility />}
                  onClick={() => fetchUserDetails(row.userId)}
                  disabled={loadingUserDetails}
                  style={{ fontSize: '12px', textTransform: 'none' }}
                >
                  View Detail
                </Button>
              )}
              <IconButton 
                onClick={() => {
                  // Close any open input boxes first
                  if (editingNotes) {
                    setNotes(row?.notes || '');
                    setEditingNotes(false);
                  }
                  if (editingMeetingUrl) {
                    setMeetingUrl(row?.meetingUrl || '');
                    setEditingMeetingUrl(false);
                  }
                  if (editingJiraUrl) {
                    setJiraUrl(row?.jiraUrl || '');
                    setEditingJiraUrl(false);
                  }
                  setOpenDialog(false);
                }} 
                size="small"
                disabled={isUpdating}
              >
                <Close />
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <ModalContentContainer>
            <LeftContent>
          <DetailsContainer>
            <DetailRow>
              <DetailLabel>Name:</DetailLabel>
              <DetailValue>{row.name || '-'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Username:</DetailLabel>
              <DetailValue>{row.username || '-'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Email:</DetailLabel>
              <DetailValue>{row.email || '-'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Role:</DetailLabel>
              <DetailValue>{row.userRole || '-'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Date & Time:</DetailLabel>
              <DetailValue>
                {(() => {
                  if (!row.createdAt && row.createdAt !== 0) return '-';
                  
                  try {
                    let date;
                    if (typeof row.createdAt === 'number') {
                      date = new Date(row.createdAt);
                    } else if (typeof row.createdAt === 'string') {
                      const numValue = parseInt(row.createdAt, 10);
                      if (!isNaN(numValue)) {
                        date = new Date(numValue);
                      } else {
                        date = new Date(row.createdAt);
                      }
                    } else {
                      return '-';
                    }
                    
                    if (isNaN(date.getTime()) || !date.getTime()) {
                      return '-';
                    }
                    
                    return date.toLocaleString();
                  } catch (e) {
                    console.error('Error formatting date in detail:', e, row.createdAt);
                    return '-';
                  }
                })()}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Feedback Content:</DetailLabel>
              <DetailValue style={{ whiteSpace: 'pre-wrap' }}>{row.content || '-'}</DetailValue>
            </DetailRow>
                <DetailRow>
                  <DetailLabel>Status:</DetailLabel>
                  <DetailValue>
                    <Select
                      value={status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      disabled={isUpdating}
                      sx={{
                        minWidth: 170,
                        maxWidth: 250,
                        height: 32,
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& .MuiSelect-select': {
                          padding: 0,
                          lineHeight: '32px',
                          fontSize: '12px',
                          textAlign: 'center',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                      renderValue={(selected) => (
                        <Chip
                          icon={getStatusIcon(selected)}
                          label={selected}
                          style={{
                            backgroundColor: getStatusColor(selected).bg,
                            color: getStatusColor(selected).color,
                            height: 28,
                          }}
                        />
                      )}
                    >
                      <MenuItem value="Not started">
                        <Chip
                          icon={<HourglassEmpty style={{ fontSize: 16 }} />}
                          label="Not started"
                          style={{ backgroundColor: '#9e9e9e', color: 'white' }}
                        />
                      </MenuItem>
                      <MenuItem value="In progress">
                        <Chip
                          icon={<EditRounded style={{ fontSize: 16 }} />}
                          label="In progress"
                          style={{ backgroundColor: '#ffc107', color: 'black' }}
                        />
                      </MenuItem>
                      <MenuItem value="Under review">
                        <Chip
                          icon={<RemoveRedEye style={{ fontSize: 16 }} />}
                          label="Under review"
                          style={{ backgroundColor: '#2196f3', color: 'white' }}
                        />
                      </MenuItem>
                      <MenuItem value="Completed">
                        <Chip
                          icon={<CheckCircle style={{ fontSize: 16 }} />}
                          label="Completed"
                          style={{ backgroundColor: '#4caf50', color: 'white' }}
                        />
                      </MenuItem>
                    </Select>
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Notes:</DetailLabel>
                  <DetailValue>
                    {editingNotes ? (
                      <EditableFieldWrapper multiline>
                        <TextField
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Enter notes"
                          InputProps={{
                            style: { fontSize: '14px' }
                          }}
                        />
                        <IconButton 
                          size="small" 
                          onClick={async () => {
                            const currentNotes = (notes || '').trim();
                            const originalNotes = (row?.notes || '').trim();
                            
                            if (currentNotes === originalNotes) {
                              setEditingNotes(false);
                              return;
                            }
                            
                            const oldNotes = row?.notes || '';
                            setIsUpdating(true);
                            try {
                              const updateData = {
                                status: status,
                                notes: currentNotes,
                                meetingUrl: meetingUrl,
                                jiraUrl: jiraUrl
                              };
                              const result = await handleUpdateFeedback(row.id, updateData);
                              if (result && result.data) {
                                showAlert('Notes updated successfully', 'success');
                                setEditingNotes(false);
                                if (onUpdateItem) {
                                  onUpdateItem(row.id, { notes: currentNotes });
                                }
                              } else {
                                showAlert('Failed to update notes', 'error');
                                setNotes(oldNotes);
                              }
                            } catch (error) {
                              console.error('Error updating notes:', error);
                              showAlert('Error updating notes', 'error');
                              setNotes(oldNotes);
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          disabled={isUpdating}
                          style={{ color: '#22c55e' }}
                        >
                          {isUpdating ? (
                            <CircularProgress size={18} style={{ color: '#22c55e' }} />
                          ) : (
                            <Check />
                          )}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setNotes(row?.notes || '');
                            setEditingNotes(false);
                          }}
                          disabled={isUpdating}
                          style={{ color: '#ef4444' }}
                        >
                          <Cancel />
                        </IconButton>
                      </EditableFieldWrapper>
                    ) : (
                      <EditableFieldWrapper>
                        <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                          {notes || '-'}
                        </span>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            // Close URL fields edit if open
                            if (editingMeetingUrl) {
                              setMeetingUrl(row?.meetingUrl || '');
                              setEditingMeetingUrl(false);
                            }
                            if (editingJiraUrl) {
                              setJiraUrl(row?.jiraUrl || '');
                              setEditingJiraUrl(false);
                            }
                            setEditingNotes(true);
                          }}
                          style={{ color: '#1976d2' }}
                        >
                          <Edit style={{ fontSize: 18 }} />
                        </IconButton>
                      </EditableFieldWrapper>
                    )}
                  </DetailValue>
                </DetailRow>
              <DetailRow>
                  <DetailLabel>Meeting URL:</DetailLabel>
                <DetailValue>
                    {editingMeetingUrl ? (
                      <EditableFieldWrapper>
                        <TextField
                          value={meetingUrl}
                          onChange={(e) => setMeetingUrl(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          placeholder="Enter meeting URL"
                          InputProps={{
                            style: { fontSize: '14px' }
                          }}
                        />
                        <IconButton 
                          size="small" 
                          onClick={async () => {
                            const oldUrl = row?.meetingUrl || '';
                            setIsUpdating(true);
                            try {
                              const updateData = {
                                status: status,
                                notes: notes,
                                meetingUrl: meetingUrl.trim(),
                                jiraUrl: jiraUrl
                              };
                              const result = await handleUpdateFeedback(row.id, updateData);
                              if (result && result.data) {
                                showAlert('Meeting URL updated successfully', 'success');
                                setEditingMeetingUrl(false);
                                if (onUpdateItem) {
                                  onUpdateItem(row.id, { meetingUrl: meetingUrl.trim() });
                                }
                              } else {
                                showAlert('Failed to update meeting URL', 'error');
                                setMeetingUrl(oldUrl);
                              }
                            } catch (error) {
                              console.error('Error updating meeting URL:', error);
                              showAlert('Error updating meeting URL', 'error');
                              setMeetingUrl(oldUrl);
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          disabled={isUpdating}
                          style={{ color: '#22c55e' }}
                        >
                          {isUpdating ? (
                            <CircularProgress size={18} style={{ color: '#22c55e' }} />
                          ) : (
                            <Check />
                          )}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setMeetingUrl(row?.meetingUrl || '');
                            setEditingMeetingUrl(false);
                          }}
                          disabled={isUpdating}
                          style={{ color: '#ef4444' }}
                        >
                          <Cancel />
                        </IconButton>
                      </EditableFieldWrapper>
                    ) : (
                      <EditableFieldWrapper>
                        <span style={{ flex: 1 }}>
                          {meetingUrl ? (
                            <a href={meetingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                              {meetingUrl}
                            </a>
                          ) : '-'}
                        </span>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            // Close Notes and Jira URL edit if open
                            if (editingNotes) {
                              setNotes(row?.notes || '');
                              setEditingNotes(false);
                            }
                            if (editingJiraUrl) {
                              setJiraUrl(row?.jiraUrl || '');
                              setEditingJiraUrl(false);
                            }
                            setEditingMeetingUrl(true);
                          }}
                          style={{ color: '#1976d2' }}
                        >
                          <Edit style={{ fontSize: 18 }} />
                        </IconButton>
                      </EditableFieldWrapper>
                    )}
                </DetailValue>
              </DetailRow>
                <DetailRow>
                  <DetailLabel>Jira URL:</DetailLabel>
                  <DetailValue>
                    {editingJiraUrl ? (
                      <EditableFieldWrapper>
                        <TextField
                          value={jiraUrl}
                          onChange={(e) => setJiraUrl(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          placeholder="Enter Jira URL manually"
                          InputProps={{
                            style: { fontSize: '14px' }
                          }}
                        />
                        <IconButton 
                          size="small" 
                          onClick={async () => {
                            const oldUrl = row?.jiraUrl || '';
                            setIsUpdating(true);
                            try {
                              const updateData = {
                                status: status,
                                notes: notes,
                                meetingUrl: meetingUrl,
                                jiraUrl: jiraUrl.trim()
                              };
                              const result = await handleUpdateFeedback(row.id, updateData);
                              if (result && result.data) {
                                showAlert('Jira URL updated successfully', 'success');
                                setEditingJiraUrl(false);
                                if (onUpdateItem) {
                                  onUpdateItem(row.id, { jiraUrl: jiraUrl.trim() });
                                }
                              } else {
                                showAlert('Failed to update Jira URL', 'error');
                                setJiraUrl(oldUrl);
                              }
                            } catch (error) {
                              console.error('Error updating Jira URL:', error);
                              showAlert('Error updating Jira URL', 'error');
                              setJiraUrl(oldUrl);
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          disabled={isUpdating}
                          style={{ color: '#22c55e' }}
                        >
                          {isUpdating ? (
                            <CircularProgress size={18} style={{ color: '#22c55e' }} />
                          ) : (
                            <Check />
                          )}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setJiraUrl(row?.jiraUrl || '');
                            setEditingJiraUrl(false);
                          }}
                          disabled={isUpdating}
                          style={{ color: '#ef4444' }}
                        >
                          <Cancel />
                        </IconButton>
                      </EditableFieldWrapper>
                    ) : (
                      <EditableFieldWrapper>
                        <span style={{ flex: 1 }}>
                          {jiraUrl ? (
                            <a href={jiraUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                              {jiraUrl}
                            </a>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={creatingJiraTicket ? <CircularProgress size={16} /> : <BugReport />}
                                onClick={async () => {
                                  setCreatingJiraTicket(true);
                                  try {
                                    const result = await handleCreateJiraTicket(row.id);
                                    if (result && result.data) {
                                      const newJiraUrl = result.data.jiraIssue?.url || result.data.feedback?.jiraUrl;
                                      setJiraUrl(newJiraUrl);
                                      showAlert('Jira ticket created successfully!', 'success');
                                      if (onUpdateItem) {
                                        onUpdateItem(row.id, { jiraUrl: newJiraUrl });
                                      }
                                    } else {
                                      showAlert('Failed to create Jira ticket', 'error');
                                    }
                                  } catch (error) {
                                    console.error('Error creating Jira ticket:', error);
                                    showAlert(error?.response?.data?.message || 'Error creating Jira ticket', 'error');
                                  } finally {
                                    setCreatingJiraTicket(false);
                                  }
                                }}
                                disabled={creatingJiraTicket || isUpdating}
                                style={{ fontSize: '12px', textTransform: 'none', marginTop: '4px', alignSelf: 'flex-start' }}
                              >
                                {creatingJiraTicket ? 'Creating...' : 'Create Jira Ticket'}
                              </Button>
                            </div>
                          )}
                        </span>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            if (editingNotes) {
                              setNotes(row?.notes || '');
                              setEditingNotes(false);
                            }
                            if (editingMeetingUrl) {
                              setMeetingUrl(row?.meetingUrl || '');
                              setEditingMeetingUrl(false);
                            }
                            setEditingJiraUrl(true);
                          }}
                          style={{ color: '#1976d2' }}
                          title={jiraUrl ? 'Edit Jira URL' : 'Add Jira URL manually'}
                        >
                          <Edit style={{ fontSize: 18 }} />
                        </IconButton>
                      </EditableFieldWrapper>
                    )}
                  </DetailValue>
                </DetailRow>
            {systemInfo && (
              <>
                <DetailRow>
                  <DetailLabel>IP Address:</DetailLabel>
                  <DetailValue>{systemInfo.ipAddress || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>User Agent:</DetailLabel>
                  <DetailValue>{systemInfo.userAgent || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Platform:</DetailLabel>
                  <DetailValue>{systemInfo.platform || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Screen Resolution:</DetailLabel>
                  <DetailValue>{systemInfo.screenResolution || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Viewport Size:</DetailLabel>
                  <DetailValue>{systemInfo.viewportSize || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Timezone:</DetailLabel>
                  <DetailValue>{systemInfo.timezone || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>URL:</DetailLabel>
                  <DetailValue>
                    {systemInfo.url ? (
                      <a href={systemInfo.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                        {systemInfo.url}
                      </a>
                    ) : '-'}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Referrer:</DetailLabel>
                  <DetailValue>{systemInfo.referrer || '-'}</DetailValue>
                </DetailRow>
              </>
            )}
            {row.systemDataUrl && (
              <DetailRow>
                <DetailLabel>System Data (JSON):</DetailLabel>
                <DetailValue>
                  <a 
                    href={row.systemDataUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ 
                      color: '#1976d2', 
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <LinkIcon style={{ fontSize: 18 }} />
                    Download System Data JSON
                  </a>
                </DetailValue>
              </DetailRow>
            )}
          </DetailsContainer>
            </LeftContent>
            {(screenshotUrls.length > 0 || row.audioUrl) && (
              <RightContent>
                {screenshotUrls.length > 0 && (
                  <ScreenshotsSection>
                    <ScreenshotsTitle>Screenshot{screenshotUrls.length > 1 ? 's' : ''}</ScreenshotsTitle>
                    <ScreenshotsGrid>
                      {screenshotUrls.map((url, index) => (
                        <ScreenshotItemModal 
                          key={index}
                          onClick={() => {
                            setSelectedImage(url);
                            setOpenImageModal(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <img src={url} alt={`Screenshot ${index + 1}`} />
                        </ScreenshotItemModal>
                      ))}
                    </ScreenshotsGrid>
                  </ScreenshotsSection>
                )}
                {row.audioUrl && (
                  <ScreenshotsSection style={{ marginTop: screenshotUrls.length > 0 ? '24px' : '0' }}>
                    <ScreenshotsTitle>Audio Recording</ScreenshotsTitle>
                    <AudioPlayerContainer>
                      <audio 
                        controls 
                        style={{ width: '100%', height: '40px' }}
                        src={row.audioUrl}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </AudioPlayerContainer>
                  </ScreenshotsSection>
                )}
              </RightContent>
            )}
          </ModalContentContainer>
        </DialogContent>
      </Dialog>

      <Dialog 
        open={openScreenshotDialog} 
        onClose={() => setOpenScreenshotDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '20px', fontWeight: 600 }}>
              Screenshot{screenshotUrls.length > 1 ? 's' : ''} ({screenshotUrls.length})
            </span>
            <IconButton onClick={() => setOpenScreenshotDialog(false)} size="small">
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <ScreenshotsContainer>
            {screenshotUrls.map((url, index) => (
              <ScreenshotItem key={index}>
                <ScreenshotImage 
                  src={url} 
                  alt={`Screenshot ${index + 1}`}
                  onClick={() => {
                    setSelectedImage(url);
                    setOpenImageModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <ScreenshotLabel>Screenshot {index + 1}</ScreenshotLabel>
              </ScreenshotItem>
            ))}
          </ScreenshotsContainer>
        </DialogContent>
      </Dialog>

      <GlobalUserDetails 
        setOpenModal={setOpenUserDetailsModal} 
        openModal={openUserDetailsModal} 
        userDetails={userDetails} 
      />

      <ImageModal 
        open={openImageModal}
        close={() => setOpenImageModal(false)}
        image={selectedImage}
      />
    </>
  );
};

export default memo(FeedbackTableRow);

const StyledTableCell = styled(TableCell)`
  text-align: left;
  color: #000000 !important;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  
  &:last-child {
    border-right: none;
  }
`;

const ContentPreview = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScreenshotButton = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  
  &:hover {
    opacity: 0.7;
  }
`;

const ScreenshotsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 0;
`;

const ScreenshotItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ScreenshotImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  background-color: #f9fafb;
  padding: 8px;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ScreenshotLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const ModalContentContainer = styled.div`
  display: flex;
  gap: 24px;
  padding: 8px 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const RightContent = styled.div`
  flex: 0 0 400px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  @media (max-width: 768px) {
    flex: 1;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  align-items: start;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const DetailValue = styled.div`
  color: #1f2937;
  font-size: 14px;
  word-break: break-word;
`;

const EditableFieldWrapper = styled.div`
  display: flex;
  align-items: ${props => props.multiline ? 'flex-start' : 'center'};
  gap: 8px;
  width: 100%;
  
  ${props => props.multiline && `
    align-items: flex-start;
    
    button {
      margin-top: 4px;
    }
  `}
`;

const ScreenshotsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 100px);
  overflow: hidden;
`;

const ScreenshotsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
  flex-shrink: 0;
`;

const ScreenshotsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ScreenshotItemModal = styled.div`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background-color: #fff;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  max-height: 200px;
  
  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
  }
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    transition: all 0.2s;
    cursor: pointer;
  }
`;

const AudioPlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

